// =====================================================
// UNIMATCHPLAY 2026 — API Layer
// All Supabase CRUD operations
// =====================================================

import { supabase } from './supabase';
import type {
  Tournament,
  Team,
  Player,
  MatchUp,
  Game,
  HoleScore,
} from '../data/types';
import { v4 as uuidv4 } from 'uuid';

// ===== TOURNAMENT =====

export async function fetchTournament(): Promise<Tournament | null> {
  const { data, error } = await supabase
    .from('tournaments')
    .select('*')
    .single();
  if (error) { console.error('fetchTournament error:', error); return null; }
  return data;
}

// ===== TEAMS =====

export async function fetchTeams(tournamentId: string): Promise<Team[]> {
  const { data, error } = await supabase
    .from('teams')
    .select('*')
    .eq('tournament_id', tournamentId)
    .order('group_name')
    .order('seed');
  if (error) { console.error('fetchTeams error:', error); return []; }
  return data ?? [];
}

export async function createTeam(team: Partial<Team> & { tournament_id: string; name: string }): Promise<Team | null> {
  const { data, error } = await supabase
    .from('teams')
    .insert({ id: uuidv4(), ...team })
    .select()
    .single();
  if (error) { console.error('createTeam error:', error); return null; }
  return data;
}

export async function updateTeam(id: string, updates: Partial<Team>): Promise<Team | null> {
  const { data, error } = await supabase
    .from('teams')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  if (error) { console.error('updateTeam error:', error); return null; }
  return data;
}

export async function deleteTeam(id: string): Promise<boolean> {
  const { error } = await supabase.from('teams').delete().eq('id', id);
  if (error) { console.error('deleteTeam error:', error); return false; }
  return true;
}

// ===== PLAYERS =====

export async function fetchPlayers(tournamentId: string): Promise<Player[]> {
  const { data, error } = await supabase
    .from('players')
    .select('*')
    .eq('tournament_id', tournamentId)
    .order('name');
  if (error) { console.error('fetchPlayers error:', error); return []; }
  return data ?? [];
}

export async function createPlayer(player: Partial<Player> & { tournament_id: string; name: string }): Promise<Player | null> {
  const { data, error } = await supabase
    .from('players')
    .insert({ id: uuidv4(), ...player })
    .select()
    .single();
  if (error) { console.error('createPlayer error:', error); return null; }
  return data;
}

export async function updatePlayer(id: string, updates: Partial<Player>): Promise<Player | null> {
  const { data, error } = await supabase
    .from('players')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  if (error) { console.error('updatePlayer error:', error); return null; }
  return data;
}

export async function deletePlayer(id: string): Promise<boolean> {
  const { error } = await supabase.from('players').delete().eq('id', id);
  if (error) { console.error('deletePlayer error:', error); return false; }
  return true;
}

/**
 * Create a player with an auth account.
 * Email pattern: jmeno.prijmeni@unimatchplay.golf
 * Password pattern: prijmeni_67
 */
export async function createPlayerWithAuth(
  player: Partial<Player> & { tournament_id: string; name: string },
  email: string,
  password: string
): Promise<Player | null> {
  // Call RPC to create auth user
  const { data: authId, error: authError } = await supabase.rpc('admin_create_auth_user', {
    user_email: email,
    user_password: password,
    user_metadata: { name: player.name },
  });

  if (authError) {
    console.error('createPlayerWithAuth auth error:', authError);
    return null;
  }

  // Create player record with auth_id
  return createPlayer({
    ...player,
    email,
    auth_id: authId,
  });
}

// ===== MATCH-UPS =====

export async function fetchMatchUps(tournamentId: string): Promise<MatchUp[]> {
  const { data, error } = await supabase
    .from('match_ups')
    .select('*')
    .eq('tournament_id', tournamentId)
    .order('sort_order');
  if (error) { console.error('fetchMatchUps error:', error); return []; }
  return data ?? [];
}

export async function updateMatchUp(id: string, updates: Partial<MatchUp>): Promise<MatchUp | null> {
  const { data, error } = await supabase
    .from('match_ups')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  if (error) { console.error('updateMatchUp error:', error); return null; }
  return data;
}

// ===== GAMES =====

export async function fetchGames(matchUpIds: string[]): Promise<Game[]> {
  if (matchUpIds.length === 0) return [];
  const { data, error } = await supabase
    .from('games')
    .select('*')
    .in('match_up_id', matchUpIds)
    .order('game_number');
  if (error) { console.error('fetchGames error:', error); return []; }
  return data ?? [];
}

export async function fetchAllGames(tournamentId: string): Promise<Game[]> {
  // Fetch all match-ups first to get their IDs
  const matchUps = await fetchMatchUps(tournamentId);
  const matchUpIds = matchUps.map(m => m.id);
  return fetchGames(matchUpIds);
}

export async function createGame(game: Partial<Game> & { match_up_id: string; type: string; game_number: number }): Promise<Game | null> {
  const { data, error } = await supabase
    .from('games')
    .insert({ id: uuidv4(), ...game })
    .select()
    .single();
  if (error) { console.error('createGame error:', error); return null; }
  return data;
}

export async function updateGame(id: string, updates: Partial<Game>): Promise<Game | null> {
  const { data, error } = await supabase
    .from('games')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  if (error) { console.error('updateGame error:', error); return null; }
  return data;
}

export async function deleteGame(id: string): Promise<boolean> {
  const { error } = await supabase.from('games').delete().eq('id', id);
  if (error) { console.error('deleteGame error:', error); return false; }
  return true;
}

// ===== HOLE SCORES =====

export async function fetchHoleScores(gameIds: string[]): Promise<HoleScore[]> {
  if (gameIds.length === 0) return [];
  const { data, error } = await supabase
    .from('hole_scores')
    .select('*')
    .in('game_id', gameIds)
    .order('hole_number')
    .order('team_side');
  if (error) { console.error('fetchHoleScores error:', error); return []; }
  return data ?? [];
}

export async function fetchAllHoleScores(tournamentId: string): Promise<HoleScore[]> {
  const games = await fetchAllGames(tournamentId);
  const gameIds = games.map(g => g.id);
  return fetchHoleScores(gameIds);
}

/**
 * Upsert a hole score (insert or update).
 * Uses UNIQUE(game_id, hole_number, team_side) for conflict resolution.
 */
export async function upsertHoleScore(
  gameId: string,
  holeNumber: number,
  teamSide: 'a' | 'b',
  strokes: number
): Promise<HoleScore | null> {
  const { data, error } = await supabase
    .from('hole_scores')
    .upsert(
      {
        id: uuidv4(),
        game_id: gameId,
        hole_number: holeNumber,
        team_side: teamSide,
        strokes,
      },
      { onConflict: 'game_id,hole_number,team_side' }
    )
    .select()
    .single();
  if (error) { console.error('upsertHoleScore error:', error); return null; }
  return data;
}

/**
 * Delete a hole score (to correct mistakes).
 */
export async function deleteHoleScore(gameId: string, holeNumber: number, teamSide: 'a' | 'b'): Promise<boolean> {
  const { error } = await supabase
    .from('hole_scores')
    .delete()
    .eq('game_id', gameId)
    .eq('hole_number', holeNumber)
    .eq('team_side', teamSide);
  if (error) { console.error('deleteHoleScore error:', error); return false; }
  return true;
}

// ===== BRACKET PROPAGATION =====

/**
 * Propagate semifinal results to afternoon matches.
 * Updates team_a_id and team_b_id on destination match-ups.
 */
export async function propagateBracket(
  propagations: { matchUpId: string; teamAId: string; teamBId: string }[]
): Promise<boolean> {
  for (const prop of propagations) {
    const { error } = await supabase
      .from('match_ups')
      .update({ team_a_id: prop.teamAId, team_b_id: prop.teamBId })
      .eq('id', prop.matchUpId);
    if (error) {
      console.error('propagateBracket error:', error);
      return false;
    }
  }
  return true;
}

// ===== AUTH =====

export async function signIn(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export async function getCurrentUser() {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
}

/**
 * Find the player record linked to an auth user.
 */
export async function findPlayerByAuthId(authId: string): Promise<Player | null> {
  const { data, error } = await supabase
    .from('players')
    .select('*')
    .eq('auth_id', authId)
    .single();
  if (error) return null;
  return data;
}

/**
 * Upload an avatar image to Supabase Storage and return the public URL.
 * Stores in the 'avatars' bucket under the player's ID.
 */
export async function uploadAvatar(playerId: string, file: File): Promise<string | null> {
  const ext = file.name.split('.').pop() ?? 'jpg';
  const path = `${playerId}.${ext}`;

  // Upload (upsert to overwrite existing)
  const { error: uploadError } = await supabase.storage
    .from('avatars')
    .upload(path, file, { upsert: true, contentType: file.type });

  if (uploadError) {
    console.error('uploadAvatar error:', uploadError);
    return null;
  }

  // Get public URL
  const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(path);

  // Update player record with avatar URL (append timestamp to bust cache)
  const avatarUrl = `${publicUrl}?t=${Date.now()}`;
  await updatePlayer(playerId, { avatar_url: avatarUrl });

  return avatarUrl;
}

/**
 * Upload a team logo image to Supabase Storage and return the public URL.
 * Stores in the 'avatars' bucket under a team-specific path.
 */
export async function uploadTeamLogo(teamId: string, file: File): Promise<string | null> {
  const ext = file.name.split('.').pop() ?? 'png';
  const path = `team_logo_${teamId}.${ext}`;

  // Upload (upsert to overwrite existing)
  const { error: uploadError } = await supabase.storage
    .from('avatars')
    .upload(path, file, { upsert: true, contentType: file.type });

  if (uploadError) {
    console.error('uploadTeamLogo error:', uploadError);
    return null;
  }

  // Get public URL
  const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(path);

  // Update team record with logo URL (append timestamp to bust cache)
  const logoUrl = `${publicUrl}?t=${Date.now()}`;
  await updateTeam(teamId, { logo_url: logoUrl });

  return logoUrl;
}

/**
 * Update a player's auth password (admin only).
 * Uses the same RPC pattern as user creation.
 */
export async function updatePlayerPassword(authId: string, newPassword: string): Promise<boolean> {
  const { error } = await supabase.rpc('admin_update_user_password', {
    target_user_id: authId,
    new_password: newPassword,
  });

  if (error) {
    console.error('updatePlayerPassword error:', error);
    return false;
  }
  return true;
}

/**
 * Update the currently logged-in user's own password.
 * Uses Supabase auth.updateUser — no admin RPC needed.
 */
export async function updateOwnPassword(newPassword: string): Promise<boolean> {
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) {
    console.error('updateOwnPassword error:', error);
    return false;
  }
  return true;
}

/**
 * Delete a player's avatar from Storage and clear avatar_url in DB.
 */
export async function deleteAvatar(playerId: string, avatarUrl: string): Promise<boolean> {
  // Extract the storage path from the public URL (everything after '/avatars/')
  try {
    const url = new URL(avatarUrl.split('?')[0]);
    const parts = url.pathname.split('/avatars/');
    if (parts.length < 2) return false;
    const path = parts[1];

    const { error: removeError } = await supabase.storage.from('avatars').remove([path]);
    if (removeError) {
      console.error('deleteAvatar storage error:', removeError);
    }
  } catch {
    console.warn('Could not parse avatar URL for deletion');
  }

  // Clear avatar_url in DB regardless
  const { error: dbError } = await supabase
    .from('players')
    .update({ avatar_url: null })
    .eq('id', playerId);

  if (dbError) {
    console.error('deleteAvatar db error:', dbError);
    return false;
  }
  return true;
}
